import { DateRangeID, TimeRange } from '@fullstory/client-api';
import { getDateRangeIdFromTimeRange } from '@fullstory/search-model';
import { SegmentDefinition, TimeRange as SegmentTimeRange } from '@fullstory/proto/fs/api/segment';
import { Scope } from '@fullstory/proto/fs/api/search';

const parseSegmentTimeRange = (segmentDefinition?: SegmentDefinition | null) => ({
  segmentStartTime: segmentDefinition?.constraints?.timeRange?.startTime,
  segmentEndTime: segmentDefinition?.constraints?.timeRange?.endTime,
});

const getSegmentDateRangeId = (segmentTimeRange: TimeRange): DateRangeID | null => {
  if (segmentTimeRange.Start && segmentTimeRange.End) {
    return getDateRangeIdFromTimeRange(segmentTimeRange);
  } else {
    return null;
  }
};

const getSegmentTimeRange = (segmentDefinition?: SegmentDefinition | null): TimeRange | null => {
  const { segmentStartTime, segmentEndTime } = parseSegmentTimeRange(segmentDefinition);
  return segmentStartTime && segmentEndTime
    ? {
        Start: segmentStartTime,
        End: segmentEndTime,
      }
    : null;
};

const isDateRangeIdCustomOrSince = (dateRangeID: DateRangeID | null) =>
  !!dateRangeID && (dateRangeID === DateRangeID.Custom || dateRangeID === DateRangeID.Since);

export const overrideSegmentTimeRange = (
  segmentDefinition: SegmentDefinition,
  timeRange: SegmentTimeRange,
): SegmentDefinition => {
  const cloned = { ...segmentDefinition };
  if (cloned.constraints) {
    cloned.constraints = {
      ...cloned.constraints,
      timeRange,
    };
  }
  if (cloned.excludeConstraints) {
    cloned.excludeConstraints = {
      ...cloned.excludeConstraints,
      timeRange,
    };
  }
  return cloned;
};

export const updateSegmentTimeRange = (
  segmentDefinition: SegmentDefinition,
  metricComputationTimeRange: SegmentTimeRange | null,
  segmentScope?: Scope,
  allowSegmentTimeRangeOverride = false,
): SegmentDefinition => {
  if (!!segmentScope && segmentScope !== Scope.SCOPE_SESSION && !allowSegmentTimeRangeOverride)
    return segmentDefinition;
  const segmentTimeRange = getSegmentTimeRange(segmentDefinition);
  const segmentDateRangeId = segmentTimeRange ? getSegmentDateRangeId(segmentTimeRange) : null;
  const updatedSegmentDefinition =
    segmentDefinition?.constraints &&
    !isDateRangeIdCustomOrSince(segmentDateRangeId) &&
    metricComputationTimeRange
      ? {
          ...segmentDefinition,
          constraints: {
            ...segmentDefinition.constraints,
            timeRange: metricComputationTimeRange,
          },
          excludeConstraints: segmentDefinition.excludeConstraints
            ? {
                ...segmentDefinition.excludeConstraints,
                timeRange: metricComputationTimeRange,
              }
            : null,
        }
      : segmentDefinition;
  return updatedSegmentDefinition;
};

export const getDefaultSegmentScope = (segmentDefinition?: SegmentDefinition | null): Scope => {
  const segmentTimeRange = getSegmentTimeRange(segmentDefinition);
  const segmentDateRangeId = segmentTimeRange ? getSegmentDateRangeId(segmentTimeRange) : null;
  // If the segment has a custom time range, set the default scope to individual
  if (isDateRangeIdCustomOrSince(segmentDateRangeId)) {
    return Scope.SCOPE_INDIVIDUAL;
  }
  return Scope.SCOPE_SESSION;
};
